// ─── EMPLOI DU TEMPS (source d'événements du calendrier actif) ────
// Porté de MoPCal (FT 45/53/99) : PLUSIEURS grilles par calendrier, chacune
// avec période + bascule actif ; grille jour (Lun→Ven) × créneau, cases
// { matière, salle, couleur, span }. Les occurrences sont GÉNÉRÉES à la volée
// (type 'edt') par services/edt.js — rien n'est écrit dans le calendrier.
// Rendu en CALQUE au-dessus du calendrier (balayage bord gauche = retour).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, PanResponder } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, Bascule, ChoixPuces, GesteRetourEcran,
  useUI, useT, useAuth, HIT, confirmerSuppression, confirmerChoix,
} from '@mop/core';
import {
  JOURS_EDT, creneauxDe, cleCase, migrerCasesConfig, migrerEDTSiBesoin,
  ecouterGrillesEDT, creerGrilleEDT, enregistrerGrilleEDT, supprimerGrilleEDT,
  casesDeSemaine,
} from './services/edt';
import FeuilleCaseEDT from './FeuilleCaseEDT';
import FeuilleGrilleEDT from './FeuilleGrilleEDT';
import FeuilleNomSalleEDT from './FeuilleNomSalleEDT';
import { emojiCategorie } from './data/categories';
import { useCalendrierActif } from './CalendrierActifContext';

const CELL_DEFAUT = 44;
const _h = (x) => { const [h, m] = String(x || '').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const _mmHH = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.round(m % 60)).padStart(2, '0')}`;
const fmt = (dk) => { if (!/^\d{4}-\d{2}-\d{2}$/.test(dk || '')) return '—'; const [y, m, d] = dk.split('-'); return `${d}/${m}/${y}`; };

export default function EcranEDT({ calId, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { calendriers } = useCalendrierActif();
  const lieux = calendriers.find(c => c.id === calId)?.lieux || [];
  const [grilles, setGrilles] = useState([]);
  // Une écoute refusée renvoyait une liste vide EN SILENCE : l'écran affichait
  // « aucune grille » alors qu'il n'était pas lisible (même patron que
  // Contacts/Journal/messagerie).
  const [erreurLecture, setErreurLecture] = useState('');
  const [grilleId, setGrilleId] = useState(null);   // grille OUVERTE ; null = liste
  const [feuilleGrille, setFeuilleGrille] = useState(null);   // { initial } création/paramètres
  const [caseEdit, setCaseEdit] = useState(null);   // { jourIdx, creneauIdx, case }
  const [presse, setPresse] = useState(null);       // contenu de case COPIÉ (FT#70)
  const [sourceCle, setSourceCle] = useState(null); // créneau SOURCE (surligné pendant la copie/le déplacement)
  const [modeDeplacer, setModeDeplacer] = useState(false); // presse-papier en mode DÉPLACEMENT
  const [iconesSeules, setIconesSeules] = useState(false); // blocs : icône de catégorie SEULE (noms masqués)
  const [renomme, setRenomme] = useState(null);     // { ji, ci, c } — feuille compacte nom/salle
  const [semAff, setSemAff] = useState(0);          // semaine AFFICHÉE (périodicité multi-semaines)
  const [sourceSem, setSourceSem] = useState(0);    // semaine de la case SOURCE (copie/déplacement)

  useEffect(() => {
    if (!calId) return undefined;
    migrerEDTSiBesoin(calId);
    setErreurLecture('');
    return ecouterGrillesEDT(calId, setGrilles, setErreurLecture);
  }, [calId]);

  const grille = grilles.find(g => g.id === grilleId) || null;
  const creneaux = useMemo(() => creneauxDe(grille), [grille]);

  // ── Périodicité multi-semaines : la grille affichée est CELLE de l'onglet ──
  const nbSem = Math.max(1, Number(grille?.nbSemaines) || 1);
  const sem = Math.min(semAff, nbSem - 1);
  const casesCour = casesDeSemaine(grille, sem);
  const cheminSem = (k) => (k > 0 ? `casesSemaines.${k}` : 'cases');
  const ecrireCases = (nouv, champsSupp = {}) =>
    enregistrerGrilleEDT(calId, grille.id, { [cheminSem(sem)]: nouv, ...champsSupp });
  useEffect(() => { setSemAff(0); }, [grilleId]);
  // Périodes de vacances du calendrier (pour l'application par période).
  const vacPeriodes = (calendriers.find(c => c.id === calId)?.vacancesConfig?.periodes || []).filter(p => p.actif !== false);

  // ── Zoom de la grille ──
  // Par défaut la hauteur d'un créneau est AJUSTÉE pour que tout l'EDT tienne
  // sur la hauteur visible ; le PINCEMENT (deux doigts) zoome/dézoome.
  const [vpH, setVpH] = useState(0);                 // hauteur visible de la zone détail
  const [cellZoom, setCellZoom] = useState(null);    // null = ajustement automatique
  const cellFit = vpH ? Math.min(64, Math.max(14, (vpH - 46) / Math.max(1, creneaux.length))) : CELL_DEFAUT;
  const CELL = cellZoom || cellFit;
  const cellRef = useRef(CELL); cellRef.current = CELL;
  const pinchRef = useRef({ d0: 0, h0: 0 });
  const pinch = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponderCapture: (e) => e.nativeEvent.touches.length >= 2,
    onMoveShouldSetPanResponderCapture: (e) => e.nativeEvent.touches.length >= 2,
    onPanResponderMove: (e) => {
      const ts = e.nativeEvent.touches;
      if (ts.length < 2) return;
      const d = Math.hypot(ts[0].pageX - ts[1].pageX, ts[0].pageY - ts[1].pageY);
      if (!pinchRef.current.d0) { pinchRef.current = { d0: d, h0: cellRef.current }; return; }
      setCellZoom(Math.min(90, Math.max(14, pinchRef.current.h0 * (d / pinchRef.current.d0))));
    },
    onPanResponderRelease: () => { pinchRef.current.d0 = 0; },
    onPanResponderTerminate: () => { pinchRef.current.d0 = 0; },
    onPanResponderTerminationRequest: () => false,
  }), []);

  // ── Actions grille ──
  const auteur = { uid: user?.uid, nom: user?.prenom || user?.email || '' };
  const enregistrerFeuille = async (champs) => {
    try {
      if (feuilleGrille?.initial) {
        // Paramètres : remapper les cases sur les nouveaux créneaux (FT#83) —
        // pour la semaine 0 ET chaque semaine de la rotation.
        const g = feuilleGrille.initial;
        const ancien = creneauxDe(g), nouveau = creneauxDe(champs);
        const maj = { ...champs, cases: migrerCasesConfig(g.cases || {}, ancien, nouveau) };
        const cs = g.casesSemaines || {};
        if (Object.keys(cs).length) {
          const out = {};
          for (const k of Object.keys(cs)) out[k] = migrerCasesConfig(cs[k] || {}, ancien, nouveau);
          maj.casesSemaines = out;
        }
        await enregistrerGrilleEDT(calId, g.id, maj, { ...auteur, titre: g.nom || champs?.nom || '' });
      } else {
        const id = await creerGrilleEDT(calId, champs, auteur);
        setGrilleId(id);
      }
    } catch (_) {}
    setFeuilleGrille(null);
  };
  const supprimerG = (g) => confirmerSuppression(t('edt.supprimerGrilleTitre'), t('edt.supprimerGrilleMsg', { nom: g.nom || '' }),
    async () => { try { await supprimerGrilleEDT(calId, g.id, { ...auteur, titre: g.nom || '' }); if (grilleId === g.id) setGrilleId(null); } catch (_) {} });

  // ── Actions case ──
  const enregistrerCase = async ({ nom, matiere, salle, couleur, categorie, heure, heureFin, lieuId, lieu }) => {
    if (!grille || !caseEdit) return;
    const ji = caseEdit.jourIdx;
    // Clé = créneau contenant l'heure de début ; span = créneaux touchés
    // (compat MoPCal) ; les heures RÉELLES sont stockées (fractions permises).
    const md = _h(heure), mf = _h(heureFin);
    let ci = creneaux.findIndex(cr => _h(cr.debut) <= md && md < _h(cr.fin));
    if (ci < 0) ci = md < _h(creneaux[0]?.debut) ? 0 : creneaux.length - 1;
    let span = 1;
    for (let j = ci; j < creneaux.length; j++) { if (_h(creneaux[j].debut) < mf) span = j - ci + 1; else break; }
    const cases = { ...casesCour };
    // Déplacement éventuel du DÉBUT : on retire l'ancien emplacement.
    if (caseEdit.case) delete cases[cleCase(ji, caseEdit.creneauIdx)];
    // PAS d'effacement par indices : un chevauchement reste VISIBLE (rouge)
    // et se résout explicitement — jamais de perte silencieuse d'un cours.
    cases[cleCase(ji, ci)] = {
      matiere, salle: salle || '', couleur, heure, heureFin,
      ...(nom ? { nom } : {}), ...(lieuId ? { lieuId, lieu: lieu || '' } : {}),
      ...(categorie ? { categorie } : {}), ...(span > 1 ? { span } : {}),
    };
    try { await ecrireCases(cases); } catch (_) {}
    setCaseEdit(null);
  };
  const collerCase = async (ji, ci) => {
    if (!grille || !presse) return;
    const durSlot = Math.max(5, _h(creneaux[0]?.fin) - _h(creneaux[0]?.debut));
    const durP = (presse.heure && presse.heureFin)
      ? Math.max(5, _h(presse.heureFin) - _h(presse.heure))
      : Math.max(1, presse.span || 1) * durSlot;
    const deb = _h(creneaux[ci].debut);
    const finGrille = _h(creneaux[creneaux.length - 1].fin);
    const finVoulue = Math.min(deb + durP, finGrille);
    // Cours déjà en place ce jour (heures réelles) — source exclue si déplacement.
    const existants = [];
    creneaux.forEach((_, i) => {
      const k = cleCase(ji, i); const c = casesCour[k];
      if (!c || !(c.nom || c.matiere)) return;
      if (modeDeplacer && sourceCle === k) return;
      const sp = Math.min(Math.max(1, c.span || 1), creneaux.length - i);
      const d0 = c.heure ? _h(c.heure) : _h(creneaux[i].debut);
      const f0 = c.heureFin ? _h(c.heureFin) : _h(creneaux[Math.min(i + sp - 1, creneaux.length - 1)].fin);
      existants.push({ k, d0, f0 });
    });
    const conflits = existants.filter(e => e.d0 < finVoulue && deb < e.f0);
    const ecrire = async (fin, aSupprimer) => {
      let span = 1;
      for (let j = ci; j < creneaux.length; j++) { if (_h(creneaux[j].debut) < fin) span = j - ci + 1; else break; }
      const cases = { ...casesCour };
      const champsSupp = {};
      if (modeDeplacer && sourceCle) {
        if (sourceSem === sem) delete cases[sourceCle];             // déplacement : source retirée
        else {                                                      // source dans une AUTRE semaine
          const src = { ...casesDeSemaine(grille, sourceSem) };
          delete src[sourceCle];
          champsSupp[cheminSem(sourceSem)] = src;
        }
      }
      aSupprimer.forEach(k => delete cases[k]);                     // cours en place supprimés (choix)
      // PAS d'effacement par indices « absorbés » : des cours créés sous une
      // autre durée de créneau ont des clés décalées de leurs heures réelles —
      // les vrais conflits passent par le popup (aSupprimer).
      const { span: _s, heure: _hZ, heureFin: _fZ, ...contenu } = presse;
      cases[cleCase(ji, ci)] = { ...contenu, heure: _mmHH(deb), heureFin: _mmHH(fin), ...(span > 1 ? { span } : {}) };
      try { await ecrireCases(cases, champsSupp); } catch (_) {}
      if (modeDeplacer) { setPresse(null); setSourceCle(null); setModeDeplacer(false); }
    };
    if (!conflits.length) { await ecrire(finVoulue, []); return; }
    // Conflit : supprimer les cours en place, RÉDUIRE le cours collé, ou annuler.
    const premierConflit = Math.min(...conflits.map(c => c.d0));
    confirmerChoix(t('edt.conflitTitre'), t('edt.conflitMsg', { n: conflits.length }), [
      { text: t('edt.conflitSupprimer'), style: 'destructive', onPress: () => ecrire(finVoulue, conflits.map(c => c.k)) },
      ...(premierConflit > deb ? [{ text: t('edt.conflitReduire'), onPress: () => ecrire(premierConflit, []) }] : []),
      { text: t('commun.annuler'), style: 'cancel' },
    ]);
  };
  const supprimerCaseA = async (ji, ci) => {
    if (!grille) return;
    const cases = { ...casesCour };
    delete cases[cleCase(ji, ci)];
    try { await ecrireCases(cases); } catch (_) {}
  };
  // Appui LONG sur un cours : menu modifier / copier / déplacer / supprimer.
  const menuCase = (ji, ci, c) => confirmerChoix(c.nom || c.matiere, t('edt.actionCreneau'), [
    { text: t('edt.renommer'), onPress: () => setRenomme({ ji, ci, c }) },
    { text: t('edt.copier'), onPress: () => { setPresse({ ...c }); setSourceCle(cleCase(ji, ci)); setSourceSem(sem); setModeDeplacer(false); } },
    { text: t('edt.deplacer'), onPress: () => { setPresse({ ...c }); setSourceCle(cleCase(ji, ci)); setSourceSem(sem); setModeDeplacer(true); } },
    { text: t('commun.supprimer'), style: 'destructive', onPress: () => supprimerCaseA(ji, ci) },
    { text: t('commun.annuler'), style: 'cancel' },
  ]);
  // Renommage SIMPLE (nom + salle) : le reste du cours (horaires, couleur,
  // catégorie, clé) est conservé tel quel.
  const renommerCase = async ({ nom, matiere, salle }) => {
    if (!grille || !renomme) return;
    const cases = { ...casesCour };
    const cle = cleCase(renomme.ji, renomme.ci);
    if (cases[cle]) cases[cle] = { ...cases[cle], matiere, salle, ...(nom ? { nom } : { nom: '' }) };
    try { await ecrireCases(cases); } catch (_) {}
    setRenomme(null);
  };
  const supprimerCase = async () => {
    if (!grille || !caseEdit) return;
    const cases = { ...casesCour };
    delete cases[cleCase(caseEdit.jourIdx, caseEdit.creneauIdx)];
    try { await ecrireCases(cases); } catch (_) {}
    setCaseEdit(null);
  };

  // ── Grille détail : colonne par jour, cases positionnées par créneau. ──
  // Deux cours qui se CHEVAUCHENT (données issues d'un changement de durée)
  // restent tous les deux VISIBLES : côte à côte avec liseré rouge — jamais
  // masqués par la fusion de l'autre.
  const rendreColonne = (ji) => {
    const cases = casesCour;
    // Pixels d'un instant : proportionnel DANS chaque créneau (un cours qui
    // finit à 13:30 sur des créneaux d'1 h remplit un DEMI-créneau).
    const pxDe = (m) => {
      let acc = 0;
      for (const cr of creneaux) {
        const d = _h(cr.debut), f = _h(cr.fin);
        if (m <= d) return acc;
        if (m >= f) { acc += CELL; continue; }
        return acc + ((m - d) / (f - d)) * CELL;
      }
      return acc;
    };
    const cours = [];
    creneaux.forEach((_, ci) => {
      const c = cases[cleCase(ji, ci)];
      if (!c || !(c.nom || c.matiere)) return;
      const span = Math.min(Math.max(1, c.span || 1), creneaux.length - ci);
      const deb = c.heure ? _h(c.heure) : _h(creneaux[ci].debut);
      const fin = c.heureFin ? _h(c.heureFin) : _h(creneaux[Math.min(ci + span - 1, creneaux.length - 1)].fin);
      cours.push({ ci, span, c, deb, fin: Math.max(fin, deb + 5) });
    });
    const chevauche = (a, b) => a.deb < b.fin && b.deb < a.fin;
    // Un créneau est OCCUPÉ si sa plage horaire intersecte un cours (heures
    // RÉELLES, pas le span stocké) → aucune cellule vide ne s'y dessine.
    const slotOccupe = (ci) => {
      const d = _h(creneaux[ci].debut), f = _h(creneaux[ci].fin);
      return cours.some(o => o.deb < f && d < o.fin);
    };
    return (
      <View key={ji} style={{ flex: 1 }}>
        <Text style={s.jourTitre}>{(t('commun.joursNoms') || [])[ji] || JOURS_EDT[ji]}</Text>
        <View style={{ height: creneaux.length * CELL }}>
          {creneaux.map((cr, ci) => {
            if (slotOccupe(ci)) return null;
            return (
              <TouchableOpacity key={ci} activeOpacity={0.7}
                style={[s.cellule, { top: ci * CELL, height: CELL - 3 },
                  presse ? { borderStyle: 'dashed', borderColor: UI.accent, borderWidth: 1 } : null]}
                onPress={() => { if (presse) collerCase(ji, ci); else setCaseEdit({ jourIdx: ji, creneauIdx: ci, case: null }); }} />
            );
          })}
          {cours.map((it) => {
            const conflits = cours.filter(o => o !== it && chevauche(o, it));
            const enConflit = conflits.length > 0;
            // Rang parmi les cours en conflit (ordre de début) → gauche/droite.
            const rang = enConflit ? [it, ...conflits].sort((a, b) => a.deb - b.deb || a.fin - b.fin).indexOf(it) : 0;
            const cle = cleCase(ji, it.ci);
            const hB = Math.max(14, pxDe(it.fin) - pxDe(it.deb) - 3);
            const ic = emojiCategorie(it.c.categorie) || '';
            return (
              <TouchableOpacity key={cle} activeOpacity={0.7}
                style={[s.cellule, s.celluleCours, { top: pxDe(it.deb), height: hB },
                  { backgroundColor: (it.c.couleur || UI.accent) + '22', borderLeftWidth: 3, borderLeftColor: it.c.couleur || UI.accent, borderWidth: 0 },
                  enConflit ? { left: rang % 2 === 0 ? 1 : '50%', right: rang % 2 === 0 ? '50%' : 1, borderWidth: 1.5, borderColor: UI.danger } : null,
                  sourceCle === cle ? { borderWidth: 2.5, borderColor: UI.accent, backgroundColor: UI.accent + '33' } : null]}
                onPress={() => setCaseEdit({ jourIdx: ji, creneauIdx: it.ci, case: it.c })}
                onLongPress={() => menuCase(ji, it.ci, it.c)}>
                {/* Nom CENTRÉ (comme la vue semaine/jour) ; mode « icônes seules » :
                    l'icône de catégorie remplace le nom (taille selon le bloc). */}
                {iconesSeules && ic ? (
                  <Text style={{ fontSize: Math.max(12, Math.min(26, Math.round(hB * 0.5))) }}>{ic}</Text>
                ) : (
                  <>
                    <Text style={[s.matiere, { color: it.c.couleur || UI.accent }]} numberOfLines={2}>{ic ? ic + ' ' : ''}{it.c.nom || it.c.matiere}</Text>
                    {!!it.c.nom && !!it.c.matiere && <Text style={s.salle} numberOfLines={1}>{it.c.matiere}</Text>}
                    {!!it.c.salle && <Text style={s.salle} numberOfLines={1}>{it.c.salle}</Text>}
                  </>
                )}
              </TouchableOpacity>
            );
          })}
          {/* Lignes de créneaux : par-dessus, visibles MÊME sous un cours qui
              s'étale sur plusieurs créneaux (repère temporel constant). */}
          {creneaux.map((_, ci) => (
            <View key={'l' + ci} pointerEvents="none" style={[s.ligneCreneau, { top: ci * CELL }]} />
          ))}
        </View>
      </View>
    );
  };

  return (
    <GesteRetourEcran actif onRetour={grilleId ? () => setGrilleId(null) : onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={grille ? (grille.nom || t('edt.titre')) : t('edt.titre')} iconeTitre="🕐"
          onRetour={grilleId ? () => setGrilleId(null) : onFermer}
          action={grille
            ? { icone: '⚙️', label: t('edt.parametres'), onPress: () => setFeuilleGrille({ initial: grille }) }
            : { icone: '＋', label: t('edt.nouvelleGrille'), onPress: () => setFeuilleGrille({ initial: null }) }}>
          {/* Bascule noms ↔ icônes seules (grisée quand les noms sont masqués). */}
          {!!grille && (
            <TouchableOpacity hitSlop={HIT} onPress={() => setIconesSeules(v => !v)} accessibilityLabel={t('edt.basculeNoms')}>
              <Text style={{ fontSize: 20, opacity: iconesSeules ? 0.35 : 1 }}>🔤</Text>
            </TouchableOpacity>
          )}
        </PageHeader>

        {/* Presse-papier : barre FIXE sous l'en-tête (visible même en défilant),
            la case SOURCE reste surlignée tant que la copie est active. */}
        {!!presse && !!grille && (
          <View style={[s.presseBarre, { marginHorizontal: 12, marginTop: 8, marginBottom: 0 }]}>
            <Text style={s.presseTxt} numberOfLines={1}>{modeDeplacer ? '⇄ ' + t('edt.deplacerBarre', { matiere: presse.nom || presse.matiere }) : '📋 ' + t('edt.collerBarre', { matiere: presse.nom || presse.matiere })}</Text>
            <TouchableOpacity hitSlop={HIT} onPress={() => { setPresse(null); setSourceCle(null); setModeDeplacer(false); }} accessibilityLabel={t('commun.fermer')}>
              <Text style={{ fontSize: 14, color: UI.accent, fontWeight: '800' }}>✕</Text>
            </TouchableOpacity>
          </View>
        )}

        {!grille ? (
          // ── LISTE des grilles ──
          <ScrollView contentContainerStyle={s.corps}>
            {!!erreurLecture && (
              <View style={s.bandeau}>
                <Text style={s.bandeauTxt}>⚠️ {t('calendrier.erreurLectureEdt')}</Text>
                <Text style={s.bandeauDet}>{erreurLecture}</Text>
              </View>
            )}
            {erreurLecture ? null : grilles.length === 0 ? (
              <EmptyState icon="🕐" texte={t('edt.aucuneGrille')} aide={t('edt.aucuneGrilleAide')} />
            ) : grilles.map(g => (
              <LigneSwipe key={g.id} espacement={8} enabled
                onEditer={() => setFeuilleGrille({ initial: g })}
                onSupprimer={() => supprimerG(g)}>
                <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => setGrilleId(g.id)}>
                  <Text style={{ fontSize: 18 }}>🕐</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.carteNom} numberOfLines={1}>{g.nom || t('edt.grille')}</Text>
                    <Text style={s.carteSous} numberOfLines={1}>{fmt(g.debut)} → {fmt(g.fin)}</Text>
                  </View>
                  <Bascule valeur={g.actif !== false} accessibilityLabel={t('edt.active')}
                    onChange={(v) => enregistrerGrilleEDT(calId, g.id, { actif: v }, { ...auteur, titre: g.nom || '' }).catch(() => {})} />
                </TouchableOpacity>
              </LigneSwipe>
            ))}
            {grilles.length > 0 && <Text style={s.aide}>{t('edt.listeAide')}</Text>}
          </ScrollView>
        ) : (
          // ── DÉTAIL : la grille type (pincement = zoom sur la hauteur) ──
          <View style={{ flex: 1 }} onLayout={(e) => setVpH(e.nativeEvent.layout.height)} {...pinch.panHandlers}>
          {/* Périodicité multi-semaines : un ONGLET par semaine de la rotation. */}
          {nbSem > 1 && (
            <View style={{ paddingHorizontal: 12, paddingTop: 8 }}>
              <ChoixPuces options={Array.from({ length: nbSem }, (_, k) => ({ id: k, label: t('edt.semaineCourte', { n: k + 1 }) }))}
                value={sem} onChange={setSemAff} />
            </View>
          )}
          <ScrollView contentContainerStyle={s.corps}>
            {grille.actif === false && <Text style={s.inactif}>{t('edt.grilleInactive')}</Text>}
            <View style={{ flexDirection: 'row', gap: 4 }}>
              <View style={{ width: 40 }}>
                <Text style={s.jourTitre}> </Text>
                <View style={{ height: creneaux.length * CELL }}>
                  {creneaux.map((cr, ci) => (
                    (CELL >= 20 || ci % 2 === 0) &&
                      <Text key={ci} style={[s.heure, { top: ci * CELL }]}>{cr.debut}</Text>
                  ))}
                </View>
              </View>
              {JOURS_EDT.map((_, ji) => rendreColonne(ji))}
            </View>
            <Text style={s.aide}>{t('edt.grilleAide')}</Text>
          </ScrollView>
          </View>
        )}

        <FeuilleGrilleEDT visible={!!feuilleGrille} initial={feuilleGrille?.initial || null} vacances={vacPeriodes}
          onEnregistrer={enregistrerFeuille} onFermer={() => setFeuilleGrille(null)} />
        <FeuilleNomSalleEDT visible={!!renomme} initial={renomme?.c || null}
          onEnregistrer={renommerCase} onFermer={() => setRenomme(null)} />
        <FeuilleCaseEDT visible={!!caseEdit} info={caseEdit} creneaux={creneaux} lieux={lieux}
          onEnregistrer={enregistrerCase} onSupprimer={supprimerCase}
          onCopier={(c) => { setPresse({ ...c }); setSourceCle(caseEdit ? cleCase(caseEdit.jourIdx, caseEdit.creneauIdx) : null); setSourceSem(sem); setModeDeplacer(false); setCaseEdit(null); }}
          onFermer={() => setCaseEdit(null)} />
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, padding: 12 },
  carteNom: { fontSize: 14.5, fontWeight: '800', color: UI.ink },
  carteSous: { fontSize: 12, color: UI.muted, marginTop: 1 },
  jourTitre: { fontSize: 11, fontWeight: '800', color: UI.muted, textAlign: 'center', marginBottom: 4 },
  heure: { position: 'absolute', right: 4, fontSize: 9.5, color: UI.faint },
  cellule: { position: 'absolute', left: 1, right: 1, borderRadius: 7, borderWidth: 0.5, borderColor: UI.border,
             backgroundColor: UI.card, padding: 3 },
  celluleCours: { alignItems: 'center', justifyContent: 'center' },
  matiere: { fontSize: 10, fontWeight: '800', textAlign: 'center' },
  salle: { fontSize: 8.5, color: UI.muted, textAlign: 'center' },
  ligneCreneau: { position: 'absolute', left: 0, right: 0, height: StyleSheet.hairlineWidth, backgroundColor: UI.border, opacity: 0.7 },
  semTabs: { flexDirection: 'row', gap: 7, paddingHorizontal: 12, paddingTop: 8 },
  semTab: { borderWidth: 1, borderColor: UI.champBord, borderRadius: 999, paddingHorizontal: 14, paddingVertical: 5, backgroundColor: UI.card },
  semTabOn: { backgroundColor: UI.accent, borderColor: UI.accent },
  semTabTxt: { fontSize: 12.5, color: UI.ink, fontWeight: '700' },
  semTabTxtOn: { color: '#fff' },
  inactif: { fontSize: 12, color: UI.danger, fontWeight: '700', marginBottom: 8, textAlign: 'center' },
  presseBarre: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.card, borderWidth: 0.5, borderColor: UI.accent, borderRadius: 10, paddingVertical: 7, paddingHorizontal: 11, marginBottom: 8 },
  presseTxt: { flex: 1, fontSize: 12, color: UI.ink, fontWeight: '600' },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 14, textAlign: 'center' },
  bandeau:    { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11, marginBottom: 10 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
});
